import { Popup } from "@/components/popups/Popup";
import { Fragment, useCallback, useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { routes } from "@/common/utils/routes";
import classes from "./ChangedEmailPopup.module.scss";
import { onCloseChangedEmailPopup } from "@/components/popup-dispatcher/PopupDispatcher";

const SUCCESS_CHANGED = "Email Changed!";
const ERROR_CHANGED = "Email change failed";

const ERROR_VERIFIED = "Unsuccessful Email Verified!";
const SUCCESS_VERIFIED = "Email Verified!";

const SUCCESS_DELETE_ACCOUNT = "Account deleted!";
const ERROR_DELETE_ACCOUNT = "Account delete failed";

const SuccessMessage = () => {
  return (
    <Fragment>
      <p>Congrats, you did it.</p>
      <p>
        Your account is now secure, and you will now receive key account
        notifications via email.
      </p>
      <p>
        If you want to change your email address, you can do so in your
        &nbsp;
        <Link to={routes.accountSettings()}>account settings</Link>.
      </p>
    </Fragment>
  );
};

const SuccessAccountDeleteMessage = () => {
  return (
    <Fragment>
      <p>Confirmed!</p>
      <p>Your RedGIFs account has now been deleted.</p>
    </Fragment>
  );
};

const ErrorAccountDeleteMessage = () => {
  return (
    <Fragment>
      <p>Unfortunately your account deletion was unsuccessful.</p>
      <p>Please email <a href="mailto:support@redgifs.com">support@redgifs.com</a> to proceed.</p>
    </Fragment>
  );
};

export const ChangedEmailPopup = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const message = useMemo(() => {
    return searchParams.get("message");
  }, [searchParams]);

  const handleClose = useCallback(() => {
    onCloseChangedEmailPopup();
    setSearchParams([]);
  }, [setSearchParams]);

  const title = useMemo(() => {
    if (message === null) {
      return "";
    }

    if (message === "email-changed") {
      return SUCCESS_CHANGED;
    }

    if (message === "email-change-taken" || message === "email-change-error") {
      return ERROR_CHANGED;
    }

    if (message === "account-deleted") {
      return SUCCESS_DELETE_ACCOUNT;
    }

    if (message === "account-delete-error") {
      return ERROR_DELETE_ACCOUNT;
    }

    if (location.hash === "#verify-email") {
      if (searchParams.get("code") === "already-verified") {
        return SUCCESS_VERIFIED;
      }

      return searchParams.get("success") === "true" ? SUCCESS_VERIFIED : ERROR_VERIFIED;
    }

    if (location.hash === "#changed-email") {
      return searchParams.get("success") === "true" ? SUCCESS_CHANGED : ERROR_CHANGED;
    }

    return "";
  }, [location.hash, message, searchParams]);

  const description = useMemo(() => {
    if (message === null) {
      return "";
    }

    if (message === "email-changed") {
      return (<SuccessMessage />);
    }

    if (message === "email-change-taken") {
      return "The email address is already taken.";
    }

    if (message === "email-change-error") {
      return "The email address could not be changed. Please try again later.";
    }

    if (message === "account-deleted") {
      return (<SuccessAccountDeleteMessage />);
    }

    if (message === "account-delete-error") {
      return (<ErrorAccountDeleteMessage />);
    }

    if (location.hash === "#verify-email" || location.hash === "#changed-email") {
      if (searchParams.get("code") === "already-verified") {
        return (<SuccessMessage />);
      }

      return searchParams.get("success") === "true" ? (<SuccessMessage />) : message;
    }

    return message;
  }, [location.hash, message, searchParams]);

  return (
    <Popup
      isOpen={true}
      onClose={handleClose}
      title={title}
    >
      <div className={classes.content}>
        {description}
      </div>
    </Popup>
  );
};
